import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search,
  ShoppingBag,
  CheckCircle,
  XCircle,
  AlertCircle,
  CreditCard,
  QrCode,
  FileText,
  RefreshCw,
  CheckCircle2,
} from 'lucide-react';
import { apiClient } from '../lib/api.js';
import { Card, CardContent } from '../components/ui/Card.js';
import { Badge } from '../components/ui/Badge.js';
import { Input } from '../components/ui/Input.js';
import {
  formatBRL,
  formatDateTime,
  formatRelative,
  cn,
  orderStatusColor,
} from '../lib/utils.js';

const statusOptions = [
  { value: '', label: 'Todos status' },
  { value: 'approved', label: 'Aprovados' },
  { value: 'pending', label: 'Pendentes' },
  { value: 'refunded', label: 'Reembolsados' },
  { value: 'chargeback', label: 'Chargeback' },
  { value: 'cancelled', label: 'Cancelados' },
];

const productKindOptions = [
  { value: '', label: 'Todos tipos' },
  { value: 'main', label: 'Principal' },
  { value: 'order_bump', label: 'Order Bump' },
  { value: 'upsell', label: 'Upsell' },
  { value: 'cross_sell', label: 'Cross-sell' },
];

const paymentMethodOptions = [
  { value: '', label: 'Todos pagamentos' },
  { value: 'credit_card', label: 'Cartão' },
  { value: 'pix', label: 'PIX' },
  { value: 'boleto', label: 'Boleto' },
];

function paymentIcon(method: string) {
  if (method === 'pix') return <QrCode className="h-3.5 w-3.5" />;
  if (method === 'credit_card') return <CreditCard className="h-3.5 w-3.5" />;
  return <FileText className="h-3.5 w-3.5" />;
}

function statusIcon(status: string) {
  if (status === 'approved') return <CheckCircle className="h-3.5 w-3.5" />;
  if (status === 'refunded' || status === 'chargeback')
    return <XCircle className="h-3.5 w-3.5" />;
  return <AlertCircle className="h-3.5 w-3.5" />;
}

export function Vendas(): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [productKind, setProductKind] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [search, setSearch] = useState('');
  const [syncResult, setSyncResult] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['sales', status, productKind, paymentMethod, search],
    queryFn: () =>
      apiClient.sales({
        status: status || undefined,
        product_kind: productKind || undefined,
        payment_method: paymentMethod || undefined,
        search: search || undefined,
        limit: 100,
      }),
  });

  const lastSyncQ = useQuery({
    queryKey: ['sales-last-sync-ticto'],
    queryFn: () => apiClient.salesLastSyncTicto(),
  });

  const syncMutation = useMutation({
    mutationFn: async () => {
      const sync = await apiClient.syncTicto();
      // Encadeia backfill ledger automático
      const backfill = await apiClient.backfillLedger().catch(() => null);
      return { sync, backfill };
    },
    onSuccess: ({ sync, backfill }) => {
      const bfMsg = backfill
        ? ` · ledger: ${backfill.jesCreated} JEs + ${backfill.eventsCreated} events`
        : '';
      setSyncResult(
        `✓ ${sync.totalFetched} vendas · ${sync.totalContactsCreated} novos contatos · ${sync.totalOrdersInserted} pedidos${bfMsg} (${sync.durationMs}ms)`,
      );
      qc.invalidateQueries({ queryKey: ['sales'] });
      qc.invalidateQueries({ queryKey: ['sales-last-sync-ticto'] });
      qc.invalidateQueries({ queryKey: ['finance-overview'] });
      qc.invalidateQueries({ queryKey: ['finance-journal-entries'] });
      setTimeout(() => setSyncResult(null), 10000);
    },
    onError: (err) => setSyncResult(`✗ ${String(err)}`),
  });

  if (isError) {
    return (
      <div className="p-8 text-destructive">Erro ao carregar vendas: {String(error)}</div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <ShoppingBag className="h-6 w-6 text-primary" />
            Vendas
          </h1>
          <p className="text-sm text-muted-foreground">
            Pedidos via Ticto · Cascata de upsells · Reconciliação financeira
          </p>
        </div>
        <button
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
          className="inline-flex items-center gap-2 rounded-md bg-primary/15 text-primary hover:bg-primary/25 px-3 py-1.5 text-sm font-medium border border-primary/30 disabled:opacity-50"
          title="Importa vendas reais do worker v2.0 nextboot-capi"
        >
          <RefreshCw
            className={cn('h-3.5 w-3.5', syncMutation.isPending && 'animate-spin')}
          />
          {syncMutation.isPending ? 'Sincronizando...' : 'Sync Ticto Real'}
        </button>
      </div>

      {syncResult && (
        <div
          className={cn(
            'rounded-md px-4 py-2 text-sm flex items-center gap-2',
            syncResult.startsWith('✓')
              ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
              : 'bg-rose-500/15 text-rose-300 border border-rose-500/30',
          )}
        >
          {syncResult.startsWith('✓') ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          {syncResult}
        </div>
      )}

      {lastSyncQ.data && lastSyncQ.data.realOrders > 0 && (
        <div className="text-[10px] text-muted-foreground">
          <strong>Dados reais Ticto:</strong> {lastSyncQ.data.realOrders} pedidos reais ·{' '}
          {lastSyncQ.data.totalOrders - lastSyncQ.data.realOrders} demo · última atualização{' '}
          {lastSyncQ.data.lastUpdated ? formatRelative(lastSyncQ.data.lastUpdated) : '—'}
        </div>
      )}

      {/* KPIs do filtro atual */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card>
            <CardContent className="p-3">
              <div className="text-xs text-muted-foreground">Receita Bruta</div>
              <div className="text-lg font-bold tabular-nums">
                {formatBRL(data.aggregate.grossCents)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="text-xs text-muted-foreground">Receita Líquida</div>
              <div className="text-lg font-bold tabular-nums">
                {formatBRL(data.aggregate.netCents)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="text-xs text-muted-foreground">Aprovadas</div>
              <div className="text-lg font-bold text-emerald-400 tabular-nums">
                {data.aggregate.approved}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="text-xs text-muted-foreground">Reembolsos</div>
              <div className="text-lg font-bold text-orange-400 tabular-nums">
                {data.aggregate.refunded}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="text-xs text-muted-foreground">Chargebacks</div>
              <div className="text-lg font-bold text-rose-400 tabular-nums">
                {data.aggregate.chargeback}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtros */}
      <Card>
        <CardContent className="p-3 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por produto, contato ou email..."
              className="pl-8"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            {statusOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            value={productKind}
            onChange={(e) => setProductKind(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            {productKindOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            {paymentMethodOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {(status || productKind || paymentMethod || search) && (
            <button
              onClick={() => {
                setStatus('');
                setProductKind('');
                setPaymentMethod('');
                setSearch('');
              }}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Limpar
            </button>
          )}
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          {isLoading && (
            <div className="px-4 py-8 text-center text-muted-foreground">Carregando...</div>
          )}
          {data && data.orders.length === 0 && (
            <div className="px-4 py-12 text-center text-muted-foreground">
              Nenhum pedido encontrado com os filtros atuais.
            </div>
          )}
          {data && data.orders.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/30">
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="px-4 py-3 font-medium">Data</th>
                    <th className="px-4 py-3 font-medium">Cliente</th>
                    <th className="px-4 py-3 font-medium">Produto</th>
                    <th className="px-4 py-3 font-medium">Tipo</th>
                    <th className="px-4 py-3 font-medium">Pgto</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium text-right">Bruto</th>
                    <th className="px-4 py-3 font-medium text-right">Líquido</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/40">
                  {data.orders.map((o) => (
                    <tr
                      key={o.id}
                      className="hover:bg-muted/30 cursor-pointer"
                      onClick={(e) => {
                        const target = e.target as HTMLElement;
                        if (target.closest('a')) return;
                        navigate(`/vendas/${o.id}`);
                      }}
                    >
                      <td className="px-4 py-2.5">
                        <div className="text-xs text-muted-foreground tabular-nums">
                          {formatDateTime(o.createdAt)}
                        </div>
                        <div className="text-[10px] text-muted-foreground/70">
                          {formatRelative(o.createdAt)}
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        {o.contactName ? (
                          <Link
                            to={`/contatos/${o.contactId}`}
                            className="text-sm font-medium hover:text-primary"
                          >
                            {o.contactName}
                          </Link>
                        ) : (
                          <span className="text-sm text-muted-foreground">—</span>
                        )}
                        <div className="text-xs text-muted-foreground truncate max-w-[180px]">
                          {o.contactEmail}
                        </div>
                        {(o.geoCity || o.geoState) && (
                          <div className="text-[10px] text-muted-foreground/70">
                            {[o.geoCity, o.geoState].filter(Boolean).join(' / ')}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="text-sm">{o.productName}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">
                          {o.externalId}
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <Badge variant="outline" className="text-[10px]">
                          {o.productKind}
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          {paymentIcon(o.paymentMethod)}
                          <span>{o.paymentMethod}</span>
                          {o.installments > 1 && (
                            <span className="text-[10px]">{o.installments}x</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <span
                          className={cn(
                            'inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium',
                            orderStatusColor[o.status] ?? 'bg-slate-500/15 text-slate-400',
                          )}
                        >
                          {statusIcon(o.status)}
                          {o.status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">
                        {formatBRL(o.amountGrossCents)}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">
                        <span className="font-medium">{formatBRL(o.netCents)}</span>
                        {Number(o.feeCents) > 0 && (
                          <div className="text-[10px] text-muted-foreground">
                            taxa {formatBRL(o.feeCents)}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data && (
            <div className="px-4 py-3 border-t border-border text-xs text-muted-foreground flex items-center justify-between">
              <span>
                Mostrando {data.orders.length} de {data.total} pedidos
              </span>
              <span>Ground truth: Ticto v2.0</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
